import { useCallback, useEffect, useRef, useState } from "react";
import { Flex } from "@radix-ui/themes";
import { Dialog } from "../../../components/shared/dialog";
import {
  BrandResponse,
  ConnectIntegrationFormPayload,
  GroupsPolicy,
  Integration,
  IntegrationName,
  IntegrationType,
  isKnowledgeBaseIntegrationName,
  isNangoSupportedIntegrationName,
  isTicketSystemIntegrationName,
  isWikiIntegrationName,
  isChatIntegrationName,
  supportedKnowledgeBase,
  supportedTicketSystems,
  supportedWiki,
  supportedChat,
} from "../../../api/integration/Integration.model";
import {
  useCreateNangoIntegration,
  useIntegrationApi,
  useCreateSmoochIntegration,
} from "../../../api/integration/Integration.api";
import { addIntegrationConfigs } from "./addIntegrationConfigs";
import AddIntegrationHeader from "./AddIntegrationHeader";
import SelectIntegration from "./select-integration/SelectIntegration";
import ConnectIntegration from "./connect-integration/ConnectIntegration";
import CreateIntegration from "./create-integration/CreateIntegration";
import styles from "./AddIntegration.module.scss";
import CommingSoonDialog from "./CommingSoonDialog";

import { integrationNameToGalleryItem } from "../../../api/integration/integrationNameToGalleryItem";
import { useIntegrationsTracking } from "../useIntegrationsTracking";
import HeadingSVG from "@/routes/onboarding/getting-started/assets/HeadingSVG";
import ConfigureBrands from "./configure-brands/ConfigureBrands";
import ConfigureCategories from "./configure-categories/ConfigureCategories";
import { apiResolver, useApi } from "@/api/useApi";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";

const addIntegrationSteps = [
  "connect",
  "create",
  "brands",
  "categories",
  "done",
];
type AddIntegrationStep = (typeof addIntegrationSteps)[number];

interface AddIntegrationProps {
  preSelectedName?: IntegrationName;
  type: IntegrationType;
  existingIntegrations: Integration[];
  reloadIntegrations: () => Promise<unknown>;
  onClose: () => void;
  allowMultiple: boolean;
}

function AddIntegration({
  preSelectedName,
  type,
  existingIntegrations,
  reloadIntegrations,
  onClose,
  allowMultiple,
}: AddIntegrationProps) {
  const {
    trackIntegrationConnected,
    trackIntegrationConfigured,
    trackIntegrationViewed,
  } = useIntegrationsTracking();
  const { api } = useApi();
  const integrationApi = useIntegrationApi(api);

  const [step, setStep] = useState<AddIntegrationStep>("create");
  const [name, setName] = useState<IntegrationName>();
  const [commingSoon, setCommingSoon] = useState<IntegrationName>();

  const { data: tenantGroups } = useQuery({
    queryKey: ["tenantGroups"],
    queryFn: integrationApi.getTenantGroupsInTicketSystem,
    enabled: type === "TICKET_SYSTEM" && step === "create",
  });

  const { data: integrationBrands } = useQuery({
    queryKey: ["integrationBrands"],
    queryFn: () =>
      apiResolver<BrandResponse[]>(api.get(`/platform/tenant-brand/all`)),
    enabled:
      type === "KNOWLEDGE_BASE" && name === "zendesk" && step === "brands",
  });

  const { mutateAsync: createIntegration } = useMutation({
    mutationKey: ["createIntegration"],
    mutationFn: integrationApi.createIntegration,
  });

  const { mutateAsync: completeTicketSystemConfiguration } = useMutation({
    mutationKey: ["completeTicketSystemConfiguration"],
    mutationFn: integrationApi.completeTicketSystemConfiguration,
  });

  const { mutateAsync: createBrandsConfiguration } = useMutation({
    mutationKey: ["createBrandsConfiguration"],
    mutationFn: integrationApi.createBrandsConfiguration,
  });

  const config = useRef(addIntegrationConfigs[type]);

  const createNangoIntegration = useCreateNangoIntegration({
    reloadIntegrations,
  });

  const createSmoochIntegration = useCreateSmoochIntegration({
    reloadIntegrations,
  });

  const handleIntegrationCreated = useCallback(
    async (name: IntegrationName) => {
      await reloadIntegrations();
      toast(
        `Integration to ${integrationNameToGalleryItem[name].title} created successfuly`
      );
      trackIntegrationConnected(type, name || "");

      if (type === "KNOWLEDGE_BASE" && name === "zendesk") {
        setStep("brands");
      } else if (allowMultiple) {
        setStep("done");
        setName(undefined);
      } else {
        onClose();
      }
    },
    [type, reloadIntegrations, onClose, allowMultiple]
  );

  const handleSelectIntegrationName = useCallback(
    (name: IntegrationName) => {
      trackIntegrationViewed(type, name);
      if (type === "TICKET_SYSTEM" && isTicketSystemIntegrationName(name)) {
        if (supportedTicketSystems.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : (setName(name), setStep("connect"));
        } else {
          setCommingSoon(name);
        }
      } else if (
        type === "KNOWLEDGE_BASE" &&
        isKnowledgeBaseIntegrationName(name)
      ) {
        if (supportedKnowledgeBase.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : (setName(name), setStep("connect"));
        } else {
          setCommingSoon(name);
        }
      } else if (type === "CHAT" && isChatIntegrationName(name)) {
        if (supportedChat.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : (setName(name), setStep("connect"));
        } else {
          setCommingSoon(name);
        }
      } else if (type === "WIKI" && isWikiIntegrationName(name)) {
        if (supportedWiki.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : (setName(name), setStep("connect"));
        } else {
          setCommingSoon(name);
        }
      }
    },
    [createNangoIntegration]
  );

  const handleConnectIntegration = useCallback(
    async (payload: ConnectIntegrationFormPayload) => {
      const { subdomain, ...secretValue } = payload;

      if (name == null) {
        return;
      }

      if (type === "TICKET_SYSTEM" && isTicketSystemIntegrationName(name)) {
        await createIntegration({
          type,
          name,
          secretValue: {
            email: secretValue.email || "",
            token: secretValue.token || "",
            tokenType: "API",
          },
          subdomain,
        });

        setStep("create");
      } else if (
        type === "KNOWLEDGE_BASE" &&
        isKnowledgeBaseIntegrationName(name)
      ) {
        await createIntegration({
          type,
          name,
          secretValue: {
            email: secretValue.email || "",
            token: secretValue.token || "",
            tokenType: "API",
          },
          subdomain,
          isConfigurationRequired: name === "zendesk",
        });

        await handleIntegrationCreated(name);
      } else if (type === "CHAT" && isChatIntegrationName(name)) {
        if (name === "smooch") {
          await createSmoochIntegration({
            appId: secretValue.appId || "",
            keyId: secretValue.keyId || "",
            secretKey: secretValue.secretKey || "",
          });
        } else {
          await createIntegration({
            type,
            name,
            secretValue: {
              email: secretValue.email || "",
              token: secretValue.token || "",
              tokenType: "API",
            },
            subdomain,
          });
        }

        await handleIntegrationCreated(name);
      } else if (type === "WIKI" && isWikiIntegrationName(name)) {
        await createIntegration({
          type,
          name,
          secretValue: {
            email: secretValue.email || "",
            token: secretValue.token || "",
            tokenType: "API",
          },
          subdomain,
        });

        await handleIntegrationCreated(name);
      }
    },
    [type, name, createIntegration, handleIntegrationCreated]
  );

  const handleCreateIntegration = useCallback(
    async (groupsPolicy: GroupsPolicy) => {
      if (name == null) {
        return;
      }

      if (type === "TICKET_SYSTEM" && isTicketSystemIntegrationName(name)) {
        await completeTicketSystemConfiguration({
          type,
          name,
          groupsPolicy,
        });

        await handleIntegrationCreated(name);
      }
    },
    [type, name, handleIntegrationCreated]
  );

  const handleIntegrationBrandConfiguration = useCallback(
    async (brandIds: string[]) => {
      if (name == null) {
        return;
      }

      if (type === "KNOWLEDGE_BASE" && isKnowledgeBaseIntegrationName(name)) {
        await createBrandsConfiguration({
          type,
          name,
          brandIds,
        });
      }

      if (allowMultiple) {
        setStep("categories");
      } else {
        setStep("categories");
      }
    },
    [type, name, allowMultiple]
  );

  const handleCategoriesConfiguration = useCallback(async () => {
    if (name == null) {
      return;
    }

    // Categories configuration is handled by the ConfigureCategories component
    // This just moves to the next step
    if (allowMultiple) {
      setStep("done");
      setName(undefined);
    } else {
      onClose();
    }
  }, [name, allowMultiple, onClose]);

  const handleBack = useCallback(() => {
    switch (step) {
      case "create":
        setStep("connect");
        break;
      case "brands":
        setStep("connect");
        break;
      case "categories":
        setStep("brands");
        break;
      default:
        break;
    }
  }, [step]);

  useEffect(() => {
    if (preSelectedName) {
      handleSelectIntegrationName(preSelectedName);
    }
  }, [preSelectedName, handleSelectIntegrationName]);

  if (config.current == null) {
    return <></>;
  }

  return (
    <>
      <Flex
        height={"100%"}
        direction={"column"}
        className={styles.AddIntegrationContainer}
      >
        <AddIntegrationHeader
          {...config.current.header}
          isGettingStarted={preSelectedName === undefined}
        />

        <Flex flexGrow={"1"} className={styles.AddIntegration}>
          {step === "done" && (
            <SelectIntegration
              {...config.current.select}
              type={type}
              onClick={handleSelectIntegrationName}
              existingIntegrations={existingIntegrations}
              isMultipleAllowed
            />
          )}

          {step === "connect" && name != null && (
            <ConnectIntegration
              type={type}
              name={name}
              onSubmit={handleConnectIntegration}
            />
          )}

          {step === "create" &&
            name != null &&
            tenantGroups?.groups != null && (
              <CreateIntegration
                name={name}
                groups={tenantGroups.groups}
                onSubmit={handleCreateIntegration}
                onBack={handleBack}
              />
            )}

          {step === "brands" && name != null && integrationBrands != null && (
            <ConfigureBrands
              name={name}
              brands={integrationBrands.map((b) => ({
                ...b,
                id: b.id.toString(),
              }))}
              onSubmit={handleIntegrationBrandConfiguration}
              onBack={handleBack}
            />
          )}

          {step === "categories" && name != null && (
            <ConfigureCategories
              name={name}
              onSubmit={handleCategoriesConfiguration}
              onBack={handleBack}
            />
          )}

          <Flex flexGrow={"1"} className={styles.Illustration}>
            <HeadingSVG />
          </Flex>
        </Flex>
      </Flex>

      {commingSoon != null && (
        <CommingSoonDialog
          name={commingSoon}
          onClose={() => setCommingSoon(undefined)}
        />
      )}
    </>
  );
}

interface AddIntegrationDialogProps {
  type: IntegrationType;
  preSelectedName?: IntegrationName;
  loading?: boolean;
  existingIntegrations: Integration[];
  onClose: () => void;
  reloadIntegrations: () => Promise<unknown>;
  allowMultiple?: boolean;
}

function AddIntegrationDialog({
  type,
  preSelectedName,
  loading = false,
  existingIntegrations,
  onClose,
  reloadIntegrations,
  allowMultiple = false,
}: AddIntegrationDialogProps) {
  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      loading={loading}
    >
      <Dialog.Content size={"large"}>
        <AddIntegration
          type={type}
          preSelectedName={preSelectedName}
          onClose={onClose}
          reloadIntegrations={reloadIntegrations}
          existingIntegrations={existingIntegrations}
          allowMultiple={allowMultiple}
        />
      </Dialog.Content>
    </Dialog>
  );
}

export default AddIntegrationDialog;
